import React, { useState, useEffect, useRef, useContext } from 'react';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { toast } from 'react-toastify';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import type { Category } from '../../../types/cards';
import { deleteCategory } from '../../../services/api/Category';
import { StyledCategoryCard } from '../ThemeComponent';
import userContext from '../../../configs/context/context';

interface CategoryCardProps {
	category: any;
	onClick: (category: Category) => void;
	fetchCategories: () => Promise<void>;
}

interface HandleEditProps {
	(category: any): void;
}

const CategoryCard: React.FC<CategoryCardProps> = ({ category, onClick, fetchCategories }) => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const [showFullDescription, setShowFullDescription] = useState(false);
	const [showReadMore, setShowReadMore] = useState(false);
	const descriptionRef = useRef<HTMLDivElement>(null);
	const slug = localStorage.getItem('slug');
	const location = window.location.pathname.split('/');
	const { Vendorme } = useContext<any>(userContext);
	const permmission = Vendorme?.permissions?.category;

	const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleEdit: HandleEditProps = (category) => {
		onClick(category);
		handleMenuClose();
	};

	const handleDelete = async () => {
		const confirmToastId = toast(
			<div>
				<p>Are you sure you want to delete this product?</p>
				<button
					onClick={async () => {
						toast.dismiss(confirmToastId);
						try {
							const response = await deleteCategory(
								location.includes(slug as string)
									? category?.slug
									: category?.categoryName?.slug
							);
							if (response.success) {
								toast.success(response.data.message);
								fetchCategories();
							}
						} catch (error) {
							toast.error('Failed to delete category');
						} finally {
							handleMenuClose();
						}
					}}
					style={{
						marginRight: '10px',
						padding: '5px 10px',
						background: 'red',
						color: 'white',
						border: 'none',
						cursor: 'pointer',
					}}
					type="button"
				>
					Yes
				</button>
				<button
					onClick={() => toast.dismiss(confirmToastId)}
					style={{
						padding: '5px 10px',
						background: 'gray',
						color: 'white',
						border: 'none',
						cursor: 'pointer',
					}}
					type="button"
				>
					No
				</button>
			</div>,
			{ autoClose: false, closeOnClick: false }
		);
	};

	const toggleDescription = () => {
		setShowFullDescription(!showFullDescription);
		if (descriptionRef.current) {
			descriptionRef.current.style.maxHeight = showFullDescription
				? '60px'
				: `${descriptionRef.current.scrollHeight}px`;
		}
	};

	useEffect(() => {
		if (descriptionRef.current) {
			setShowReadMore(
				descriptionRef.current.scrollHeight > descriptionRef.current.clientHeight
			);
		}
	}, [category]);

	const getImageUrl = (path: string) => {
		if (!path) return '/assets/image-not-found.webp';
		if (path.startsWith('http')) return path;
		return `${process.env.REACT_APP_BASE_IMAGE_URL}${path}`;
	};

	return (
		<StyledCategoryCard>
			<div style={{ position: 'relative' }}>
				<CardMedia
					component="img"
					height="200"
					image={
						typeof category?.categoryName?.categoryImage === 'string'
							? getImageUrl(category?.categoryName?.categoryImage)
							: category?.categoryName?.categoryImage
								? URL.createObjectURL(category?.categoryName?.categoryImage)
								: '/assets/image-not-found.webp'
					}
					alt={category.categoryName?.name}
				/>

				{category?.categoryName?.createdByModel === 'Vendor' &&
				location?.includes(slug as string) &&
				(permmission?.edit || permmission?.delete) ? (
					<IconButton
						aria-label="more"
						aria-controls="long-menu"
						aria-haspopup="true"
						onClick={handleMenuClick}
						style={{ position: 'absolute', top: 0, right: 0 }}
					>
						{anchorEl ? <MoreVertIcon /> : <MoreHorizIcon />}
					</IconButton>
				) : (
					!location.includes(slug as string) && (
						<IconButton
							aria-label="more"
							aria-controls="long-menu"
							aria-haspopup="true"
							onClick={handleMenuClick}
							style={{ position: 'absolute', top: 0, right: 0 }}
						>
							{anchorEl ? <MoreVertIcon /> : <MoreHorizIcon />}
						</IconButton>
					)
				)}
			</div>
			<CardContent sx={{ paddingBottom: '16px!important' }}>
				<Typography
					gutterBottom
					component="div"
					style={{
						fontFamily: 'Poppins',
						fontWeight: '700',
						fontSize: '18px',
						whiteSpace: 'nowrap',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
					}}
				>
					{category.categoryName.name}({category?.productCount})
				</Typography>
				<div
					ref={descriptionRef}
					style={{
						maxHeight: '60px',
						overflow: 'hidden',
						transition: 'max-height 0.3s ease',
					}}
				>
					<Typography
						variant="body1"
						color="text.secondary"
						style={{
							fontFamily: 'Poppins',
							fontWeight: '500',
							fontSize: '12px',
							color: '#658E81',
							display: '-webkit-box',
							WebkitLineClamp: showFullDescription ? 'none' : 4,
							WebkitBoxOrient: 'vertical',
						}}
					>
						{category?.categoryName?.description}
					</Typography>
				</div>
				{showReadMore && (
					<Typography
						variant="body2"
						color="primary"
						onClick={toggleDescription}
						style={{
							cursor: 'pointer',
							fontFamily: 'Poppins',
							fontWeight: '500',
							fontSize: '12px',
						}}
					>
						{showFullDescription ? 'Read Less' : 'Read More'}
					</Typography>
				)}
				{location?.includes(slug as string) &&
				(permmission?.edit || permmission?.delete) ? (
					<Menu
						id="long-menu"
						anchorEl={anchorEl}
						open={open}
						onClose={handleMenuClose}
						slotProps={{
							paper: {
								style: {
									maxHeight: 48 * 4.5,
									width: '20ch',
									fontFamily: 'Poppins',
									paddingBottom: '0px',
									paddingTop: '0px',
								},
							},
						}}
						className="product-card-menu"
					>
						{permmission?.edit && (
							<MenuItem
								onClick={() => handleEdit(category)}
								sx={{ fontFamily: 'Poppins', '&:hover': { color: '#fff' } }}
							>
								Edit
							</MenuItem>
						)}

						{permmission?.delete && (
							<MenuItem
								onClick={handleDelete}
								sx={{ fontFamily: 'Poppins', '&:hover': { color: '#fff' } }}
							>
								Delete
							</MenuItem>
						)}
					</Menu>
				) : (
					!location.includes(slug as string) &&
					(!permmission?.edit || !permmission?.delete) && (
						<Menu
							id="long-menu"
							anchorEl={anchorEl}
							open={open}
							onClose={handleMenuClose}
							slotProps={{
								paper: {
									style: {
										maxHeight: 48 * 4.5,
										width: '20ch',
										fontFamily: 'Poppins',
										paddingBottom: '0px',
										paddingTop: '0px',
									},
								},
							}}
							className="product-card-menu"
						>
							<MenuItem
								onClick={() => handleEdit(category)}
								sx={{ fontFamily: 'Poppins', '&:hover': { color: '#fff' } }}
							>
								Edit
							</MenuItem>

							<MenuItem
								onClick={handleDelete}
								sx={{ fontFamily: 'Poppins', '&:hover': { color: '#fff' } }}
							>
								Delete
							</MenuItem>
						</Menu>
					)
				)}
			</CardContent>
		</StyledCategoryCard>
	);
};

export default CategoryCard;
